import { createElement } from 'react';
import { BiSolidBook, BiSolidEnvelope } from 'react-icons/bi';

import type { OrderCoreMetricKey } from '@hopdrive/feature-order-index';
import type { DiscoverOffer, DiscoverZone, HeroIdentity, HeroProductRow, HeroView, HeroZone, HomeBucket, HomeFigure, HomeLayout, ProductCardProps, ProductCardsZone, RooftopSummaryProps } from '@hopdrive/feature-portal-home';
import { PRODUCT_ADVERTISE_CONTENT, productName } from '@hopdrive/feature-portal-products';

import type { OrderType, ProductKey } from '@features/products/catalog';
import { BUCKET_KEYS, type ProductCountBuckets, sumBuckets } from '@features/products/counts/productCounts';
import type { PortalProductEntry } from '@features/products/types';
import { reasonText } from '@features/products/ui/productCopy';

import { type HomeCounts, bucketsFor, truncatedFor } from './homeCounts';
import type { HomeScopeOption } from './utils/homeScopeSelection';
import { productRootPath } from '@features/products/productRoutes';

// Pure mapping from counts to the renderers' props. Every link carries its path as data, and the
// app renders it through its router's Link. The one callback is the rooftop card's select.

/** Identifiers spell cancelled with two Ls, copy says Canceled. */
const FIGURE_LABELS = { upcoming: 'Upcoming', active: 'Active', completed: 'Completed', cancelled: 'Canceled' } as const;

export function unitLabel(orderTypes: OrderType[], operational: boolean): string {
  if (operational) return 'jobs';
  if (orderTypes.length === 1 && orderTypes[0] === 'guest-rides') return 'rides';
  return 'orders';
}

/** `truncated` marks every figure a floor. */
export function toFigures(buckets: ProductCountBuckets, truncated: boolean): HomeFigure[] {
  return BUCKET_KEYS.map(id => ({
    id,
    label: FIGURE_LABELS[id],
    value: buckets[id],
    ...(truncated ? { truncated: true } : {}),
  }));
}

/** Typed against both packages' unions, so a change to either fails to compile here. */
export const BUCKET_METRIC: Record<HomeBucket, OrderCoreMetricKey> = { upcoming: 'upcoming', active: 'active', completed: 'completed', cancelled: 'cancelled' };

/** Only Valet has a deep-linkable Orders tab, so every other product's figure lands on its root. */
export function figureTarget(orderType: OrderType, bucket: HomeBucket): string {
  return orderType === 'moves' ? `${productRootPath('moves')}?metric=${BUCKET_METRIC[bucket]}` : productRootPath(orderType);
}

export function linkFigures(figures: HomeFigure[], orderType: OrderType): HomeFigure[] {
  return figures.map(figure => ({ ...figure, to: figureTarget(orderType, figure.id) }));
}

export function combinedFigures(counts: HomeCounts, orderTypes: OrderType[]): HomeFigure[] {
  return toFigures(
    sumBuckets(orderTypes.map(type => bucketsFor(counts, type))),
    orderTypes.some(type => truncatedFor(counts, type)),
  );
}

/** The entries Home reads a product's name from (Valet's varies with its held keys). */
export type HomeEntries = Partial<Record<ProductKey, PortalProductEntry>>;

/** One product's row in the hero's Split view; its figures link into the product. */
export function toProductRow(counts: HomeCounts, entries: HomeEntries, orderType: OrderType): HeroProductRow {
  return {
    id: orderType,
    product: orderType,
    label: productName(orderType, entries[orderType]?.heldModes),
    to: productRootPath(orderType),
    figures: linkFigures(toFigures(bucketsFor(counts, orderType), truncatedFor(counts, orderType)), orderType),
  };
}

/** The single shape's identity: the one product, its name the link into it. */
export function toHeroIdentity(entries: HomeEntries, orderType: OrderType): HeroIdentity {
  return { product: orderType, label: productName(orderType, entries[orderType]?.heldModes), to: productRootPath(orderType) };
}

/**
 * The hero: combined figures for every shape, the switch and the product rows for the multi shape,
 * the identity for the single. The single shape's figures link into its one product; the multi
 * shape's combined figures span products and carry no target.
 */
export function toHeroZone(counts: HomeCounts, entries: HomeEntries, orderTypes: OrderType[], view: HeroView, layout: HomeLayout, unit: string): HeroZone {
  const multi = layout === 'multi';
  const figures = combinedFigures(counts, orderTypes);
  return {
    key: 'hero',
    view,
    showSwitch: multi,
    unitLabel: unit,
    figures: multi ? figures : linkFigures(figures, orderTypes[0]),
    products: multi ? orderTypes.map(type => toProductRow(counts, entries, type)) : [],
    ...(multi ? {} : { identity: toHeroIdentity(entries, orderTypes[0]) }),
  };
}

export function stateHint(entry: PortalProductEntry | undefined): string | undefined {
  if (!entry) return undefined;
  const reason = reasonText(entry.reason);
  if (entry.state === 'read_only') return reason ? `Read only: ${reason}` : 'Read only';
  if (entry.state === 'limited') return reason ? `Limited: ${reason}` : 'Limited';
  return undefined;
}

/** One card per order type in play; the card is the link, so its figures carry no target. */
export function toProductCards(counts: HomeCounts, entries: HomeEntries, orderTypes: OrderType[]): ProductCardsZone {
  const cards: ProductCardProps[] = orderTypes.map(type => {
    const hint = stateHint(entries[type]);
    return {
      id: type,
      product: type,
      label: productName(type, entries[type]?.heldModes),
      to: productRootPath(type),
      figures: toFigures(bucketsFor(counts, type), truncatedFor(counts, type)),
      ...(hint ? { stateHint: hint } : {}),
    };
  });
  return { key: 'product_cards', cards };
}

/**
 * The discover hero for a customer who owns no order type: the marketed products as offer cards
 * (each linking to its page, its pitch the product's marketing lede), plus the primary "email a
 * rep" action and the Learning Center link. Empty offers → the title, intro, and actions alone.
 */
export function toDiscoverZone(input: { offered: OrderType[]; entries: HomeEntries; onContactSales: () => void }): DiscoverZone {
  const { offered, entries, onContactSales } = input;
  const offers: DiscoverOffer[] = offered.map(type => ({
    id: type,
    product: type,
    label: productName(type, entries[type]?.heldModes),
    pitch: PRODUCT_ADVERTISE_CONTENT[type].lede,
    to: productRootPath(type),
  }));
  return {
    key: 'discover',
    title: 'Welcome to the HopDrive Portal',
    intro:
      offers.length > 0
        ? 'You don’t have any products active yet. Here’s what HopDrive can do for your rooftop. Take a look, then request to turn on what fits.'
        : 'You don’t have any products active yet. Email a HopDrive Rep to see what fits your rooftop, or explore how it works.',
    offers,
    actions: [
      { key: 'contact', label: 'Email a HopDrive Rep', icon: createElement(BiSolidEnvelope), primary: true, onSelect: onContactSales },
      { key: 'learning', label: 'Learning Center', icon: createElement(BiSolidBook), to: '/learning-center' },
    ],
  };
}

export function toRooftopSummaries(counts: HomeCounts, options: HomeScopeOption[], onSelect: (id: number) => void): RooftopSummaryProps[] {
  const byId = new Map(counts.byRooftop.map(rooftop => [rooftop.id, rooftop]));
  return options.map(option => {
    const c = byId.get(option.id);
    return { id: option.id, label: option.label, orders: c?.orders ?? 0, active: c?.active ?? 0, cancelled: c?.cancelled ?? 0, onSelect };
  });
}
